import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserGraduate,
  faEnvelope,
  faPhone,
  faGraduationCap,
  faBook,
  faMale,
  faFemale,
  faCheckCircle,
  faExclamationCircle,
  faTimes,
  faArrowLeft,
  faPaperPlane,
  faSpinner,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { toastHelper } from "../../../Helpers/toastHelper";
import ApiServices from "../../Services/ApiServices";
import Loader from "../../Shared/Loader";
import RafiqChatBot from "../../Shared/RafiqChatBot";
import { storageHelper } from "../../../Helpers/storageHelper";
import botImage from "../../../assets/images/bot.png";
import { extractHost, getFullFileUrl } from "../../../Helpers/UrlHelpers";
import stringHelper from "../../../Helpers/stringHelper";
import { useLanguage } from '../../../contexts/LanguageContext';
import { encryptData } from "../../../utils/encryption";

const StudentDetails = () => {
  const { t } = useLanguage();
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiHost, setApiHost] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [chatBotLoading, setChatBotLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState("");
  const [sendingReport, setSendingReport] = useState(false);

  // Fetch student data
  const fetchStudent = async () => {
    try {
      const response = await ApiServices.get(`Dashboard/StudentProfile/${studentId}`);
      if (response.check) {
        setStudent(response.data);
        setApiHost(extractHost(response.data.host || ''));
        setNotFound(false);
      } else {
        setNotFound(true);
        setStudent(null);
      }
    } catch (error) {
      setNotFound(true);
      setStudent(null);
      toastHelper.error(t('studentDataFetchFailed'));
    }
  };

  useEffect(() => {
    if (studentId) {
      setLoading(true);
      fetchStudent().finally(() => {
        setLoading(false);
      });
    }
  }, [studentId]);

  const getTeacherName = () => {
    const fullName = storageHelper.getItem("full_name");
    return fullName || t('teacherTitleFallback');
  };

  const handleOptionSelect = async (option) => {
    if (option.value === 'yes') {
      try {
        setChatBotLoading(true);
        const response = await ApiServices.get(`Student/ExamAnalysis/${studentId}`);
        const analysisText = (response && response.data) || (typeof response === 'string' ? response : t('analysisGeneratedSuccess'));
        setAnalysisData(analysisText);
        setShowAnalysis(false);
        toastHelper.success(t('analysisSuccess'));

        return {
          message: t('analysisGeneratedSuccess')
        };
      } catch (error) {
        toastHelper.error(t('analysisFetchError'),
          {
            duration: 7000,
          }
        )
      } finally {
        setChatBotLoading(false);
      }
    } else if (option.value === 'no') {
      return {
        message: t('chatBotHelpMessage')
      };
    }
  };

  const handleSendReportToParent = async () => {
    if (!analysisData || !studentId) return;

    try {
      setSendingReport(true);
      const cleanReport = analysisData
        .replace(/<[^>]*>?/gm, '') 
        .replace(/^[ \t]+/gm, '')    
        .replace(/\n\s*\n\s*\n/g, '\n\n') 
        .trim();

      const payload = {
        report: cleanReport,
        user_id: parseInt(studentId),
      };

      const response = await ApiServices.post(`WhatsApp/ParentReport/Send`, payload);
      if (response === true) {
        toastHelper.success(t('reportSentSuccess'));
      } else {
        toastHelper.error(t('reportSentFailed'));
      }
    } catch (error) {
      toastHelper.error(t('reportSentError'));
    } finally {
      setSendingReport(false);
    }
  };


  const handleImageClick = () => {
    if (student?.image) {
      setSelectedImage(student);
    }
  };

  const closeImagePreview = () => {
    setSelectedImage(null);
  };

  const getGenderText = (gender) => {
    return gender === 1 ? t('male') : t('female');
  };

  const getGenderIcon = (gender) => {
    return gender === 1 ? faMale : faFemale;
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
        {isActive ? (
          <><FontAwesomeIcon icon={faCheckCircle} />{t('active')}</>
        ) : (
          <><FontAwesomeIcon icon={faExclamationCircle} />{t('inactive')}</>
        )}
      </span>
    );
  };

  if (loading) {
    return <Loader text={t('loadingStudentData')} />;
  }

  if (notFound || !student) {
    return (
      <div className="student-details not-found">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="not-found-content glass-card"
        >
          <FontAwesomeIcon icon={faUserGraduate} className="not-found-icon" />
          <h2>{t('studentNotFoundTitle')}</h2>
          <p>{t('studentNotFoundMessage')}</p>
          <button className="back-btn" onClick={() => navigate('/teacher/dashboard?tab=StudentManager')}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        </motion.div>
      </div>
    );
  }

  const welcomeMessage = t('chatBotWelcomeTeacher')
    ?.replace('{teacherName}', getTeacherName())
    ?.replace('{studentName}', student?.full_name || '');

  const handleShareQr = () => {
    if (!student) return;
    const studentInfo = {
      studentId: student.id,
      fullName: student.full_name,
      email: student.email,
      phone: student.phone_number,
      userClass: student.user_class_name,
      material: student.material,
    };
    const encryptedData = encryptData(studentInfo);
    const params = new URLSearchParams({
      info: encryptedData,
      title: `رمز QR للطالب: ${student.full_name}`,
      subtitle: "رمز مشفر يحتوي على كافة البيانات المعتمدة للطالب",
    });
    window.open(`/share-qr?${params.toString()}`, "_blank");
  };

  return (
    <div className="student-details">

      
      {/* Top Navigation Bar */}
      <motion.div
        className="top-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <motion.button
          className="nav-back-btn glass-button"
          onClick={() => navigate('/teacher/dashboard?tab=StudentManager')}
          whileHover={{ x: -3, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </motion.button>

        <motion.button
          className="nav-back-btn glass-button"
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: '#fff', border: 'none' }}
          onClick={handleShareQr}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faShare} />
          <span>مشاركة الرمز المشفر</span>
        </motion.button>
      </motion.div>


      {/* Hero Profile Card */}
      <motion.div
        className="hero-card glass-panel"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="hero-content">
          <motion.div
            className="avatar-wrapper"
            whileHover={{ scale: 1.05 }}
            onClick={handleImageClick}
          >
            {student.image ? (
              <img
                src={getFullFileUrl(student.image)}
                alt={student.full_name}
                className="avatar-image"
              />
            ) : (
              <div className="avatar-placeholder">
                <FontAwesomeIcon icon={faUserGraduate} />
              </div>
            )}
            <div className="avatar-status">
              {getStatusBadge(student.is_active)}
            </div>
          </motion.div>

          <div className="hero-info">
            <h1 className="student-name">{student.full_name}</h1>
            <p className="student-meta">
              <span className="meta-badge">
                <FontAwesomeIcon icon={faGraduationCap} />
                {student.user_class_name || t('notSpecified')}
              </span>
              <span className="meta-badge">
                <FontAwesomeIcon icon={faBook} />
                {student.material || t('notSpecified')}
              </span>
            </p>

            <div className="contact-chips">
              <div className="chip glass-chip">
                <div className="chip-icon"><FontAwesomeIcon icon={faEnvelope} /></div>
                <span>{student.email}</span>
              </div>
              <div className="chip glass-chip">
                <div className="chip-icon"><FontAwesomeIcon icon={faPhone} /></div>
                <span>{student.phone_number}</span>
              </div>
              <div className="chip glass-chip">
                <div className="chip-icon"><FontAwesomeIcon icon={getGenderIcon(student.gender)} /></div>
                <span>{getGenderText(student.gender)}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Content Section */}
      <motion.div
        className="details-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="detail-section glass-panel">
          <h3 className="section-title">
            <div className="title-icon"><FontAwesomeIcon icon={faUserGraduate} /></div>
            {t('basicInformation')}
          </h3>
          <div className="detail-rows">
            <div className="detail-row">
              <span className="row-label">{t('fullName')}</span>
              <span className="row-value">{student.full_name}</span>
            </div>
            <div className="detail-row">
              <span className="row-label">{t('email')}</span>
              <span className="row-value">{student.email}</span>
            </div>
            <div className="detail-row">
              <span className="row-label">{t('phone')}</span>
              <span className="row-value">{student.phone_number}</span>
            </div>
            <div className="detail-row">
              <span className="row-label">{t('gender')}</span>
              <span className="row-value">
                <FontAwesomeIcon icon={getGenderIcon(student.gender)} />
                {getGenderText(student.gender)}
              </span>
            </div>
          </div>
        </div>

        <div className="detail-section glass-panel">
          <h3 className="section-title">
            <div className="title-icon"><FontAwesomeIcon icon={faGraduationCap} /></div>
            {t('signupAcademicInfo')}
          </h3>
          <div className="detail-rows">
            <div className="detail-row">
              <span className="row-label">{t('grade')}</span>
              <span className="row-value">
                <FontAwesomeIcon icon={faGraduationCap} />
                {student.user_class_name || t('notSpecified')}
              </span>
            </div>
            <div className="detail-row">
              <span className="row-label">{t('examMaterial')}</span>
              <span className="row-value">
                <FontAwesomeIcon icon={faBook} />
                {student.material || t('notSpecified')}
              </span>
            </div>
            <div className="detail-row">
              <span className="row-label">{t('accountStatus')}</span>
              <span className="row-value">{getStatusBadge(student.is_active)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* AI Analysis Section */}
      <AnimatePresence>
        {analysisData && (
          <motion.div
            className="analysis-section"
            initial={{ opacity: 0, y: 20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="analysis-card glass-panel">
              <div className="analysis-header">
                <div className="analysis-icon-wrapper">
                  <img src={botImage} alt="Rafiq AI" className="analysis-bot-icon" />
                </div>
                <div className="analysis-title">
                  <h3 className="text-gradient">{t('smartPerformanceAnalysisReport')}</h3>
                  <span className="analysis-subtitle">{t('generatedByRafiqAI')}</span>
                </div>
              </div>
              <div
                className="analysis-content-body"
                dangerouslySetInnerHTML={{ __html: stringHelper.convertReactStringToHtml(analysisData) }}
              />

              <div className="analysis-actions">
                <motion.button
                  className="send-report-btn"
                  onClick={handleSendReportToParent}
                  disabled={sendingReport}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {sendingReport ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      {t('sendingReport')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      {t('sendReportToParentWhatsApp')}
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="modal-overlay glass-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeImagePreview}
          >
            <motion.div
              className="modal-content glass-modal"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>{selectedImage.full_name}</h3>
                <button className="modal-close" onClick={closeImagePreview}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body">
                <img
                  src={getFullFileUrl(selectedImage.image)}
                  alt={selectedImage.full_name}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ChatBot Trigger */}
      <motion.button
        className="rafiq-chatbot-trigger"
        onClick={() => setShowAnalysis(true)}
        whileHover={{ scale: 1.15, rotate: [0, -10, 10, -10, 0] }}
        whileTap={{ scale: 0.95 }}
        animate={{
            y: [0, -15, 0],
        }}
        transition={{
            y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
        }}
        title={t('analyzeStudentPerformance')}
      >
        <div className="chatbot-ring"></div>
        <img src={botImage} alt="Rafiq" className="chatbot-trigger-icon" />
      </motion.button>

      <RafiqChatBot
        isOpen={showAnalysis}
        onClose={() => setShowAnalysis(false)}
        isAIChat={false}
        enableChat={false}
        welcomeMessage={welcomeMessage}
        options={[
          { text: t('yesAnalyzePerformance'), value: "yes" },
          { text: t('noThankYou'), value: "no" }
        ]}
        onOptionSelect={handleOptionSelect}
        externalLoading={chatBotLoading}
      />

      <style jsx>{`
        * {
          font-family: "Zain", sans-serif;
        }

        .student-details {
          min-height: 100vh;
          background: transparent;
          padding: 24px;
          position: relative;
          overflow: hidden;
        }

        /* Glassmorphism Utilities */
        .glass-panel {
          background: white;
          border: 1px solid rgba(255, 255, 255, 1);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          border-radius: 32px;
          position: relative;
          z-index: 1;
        }
        .glass-button {
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
        }
        .glass-chip {
          background: #F8FAFC;
          border: 1px solid #F1F5F9;
        }

        .text-gradient {
          background: linear-gradient(135deg, #4f46e5 0%, #ec4899 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          display: inline-block;
        }

        /* Top Navigation */
        .top-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          position: relative;
          z-index: 10;
        }

        .nav-back-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 24px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        /* ChatBot Trigger Button */
        .rafiq-chatbot-trigger {
          position: fixed;
          bottom: 2rem;
          left: 2rem;
          width: 80px;
          height: 80px;
          background: transparent;
          color: white;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .chatbot-trigger-icon {
          width: 100%;
          height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 10px 15px rgba(0,0,0,0.2));
        }

        .chatbot-ring {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(124, 58, 237, 0.4) 0%, rgba(124, 58, 237, 0) 70%);
          animation: pulse-glow-ring 2s infinite ease-in-out;
          z-index: -1;
        }

        @keyframes pulse-glow-ring {
          0% { transform: scale(0.9); opacity: 0.6; }
          50% { transform: scale(1.6); opacity: 1; box-shadow: 0 0 25px rgba(124, 58, 237, 0.8); }
          100% { transform: scale(0.9); opacity: 0.6; }
        }

        @media (max-width: 768px) {
          .rafiq-chatbot-trigger {
            bottom: 1rem;
            right: 1rem;
            left: auto;
            width: 70px;
            height: 70px;
          }
        }

        /* Analysis Section */
        .analysis-section {
          margin-top: 32px;
          margin-bottom: 32px;
          position: relative;
          z-index: 2;
        }

        .analysis-card {
          overflow: hidden;
        }

        .analysis-header {
          padding: 32px;
          display: flex;
          align-items: center;
          gap: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.5);
          background: rgba(255, 255, 255, 0.3);
        }

        .analysis-icon-wrapper {
          width: 64px;
          height: 64px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.2);
          padding: 8px;
        }

        .analysis-bot-icon {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .analysis-title h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 800;
        }

        .analysis-subtitle {
          font-size: 0.95rem;
          color: #64748b;
          font-weight: 600;
        }

        .analysis-content-body {
          padding: 32px;
          font-size: 1.1rem;
          line-height: 1.8;
          color: #334155;
        }

        .analysis-actions {
          padding: 0 32px 32px;
          display: flex;
          justify-content: flex-start;
        }

        .send-report-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
          transition: all 0.2s ease;
        }

        .send-report-btn:disabled {
          background: #94a3b8;
          box-shadow: none;
          cursor: not-allowed;
        }

        /* Hero Card */
        .hero-card {
          margin-bottom: 32px;
          padding: 32px;
        }

        .hero-content {
          display: flex;
          gap: 32px;
          align-items: center;
        }

        .avatar-wrapper {
          position: relative;
          flex-shrink: 0;
          cursor: pointer;
        }

        .avatar-image,
        .avatar-placeholder {
          width: 140px;
          height: 140px;
          border-radius: 50%;
          border: 4px solid white;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
        }

        .avatar-image {
          object-fit: cover;
        }

        .avatar-placeholder {
          background: #EEF2FF;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6366f1;
          font-size: 56px;
        }

        .avatar-status {
          position: absolute;
          bottom: 4px;
          right: 4px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .status-badge.active {
          background: #10b981;
          color: white;
        }

        .status-badge.inactive {
          background: #ef4444;
          color: white;
        }

        .hero-info {
          flex: 1;
        }

        .student-name {
          margin: 0 0 12px;
          font-size: 36px;
          font-weight: 900;
          color: #0F172A;
          letter-spacing: -0.5px;
        }

        .student-meta {
          margin: 0 0 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .meta-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(99, 102, 241, 0.1);
          color: #4f46e5;
          border-radius: 12px;
          font-weight: 700;
          font-size: 14px;
        }

        .contact-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }

        .chip {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 20px;
          border-radius: 16px;
          font-size: 15px;
          font-weight: 600;
          color: #334155;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
        }

        .chip-icon {
          width: 32px;
          height: 32px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6366f1;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
        }

        /* Details Grid */
        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 32px;
          position: relative;
          z-index: 2;
        }

        .detail-section {
          padding: 32px;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 0 0 24px;
          font-size: 20px;
          font-weight: 800;
          color: #1e293b;
        }

        .title-icon {
          width: 40px;
          height: 40px;
          background: #EEF2FF;
          color: #6366f1;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
        }

        .detail-rows {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;
          border-bottom: 1px dashed rgba(148, 163, 184, 0.4);
        }

        .detail-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .row-label {
          font-size: 15px;
          color: #64748b;
          font-weight: 600;
        }

        .row-value {
          font-size: 16px;
          color: #0f172a;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .row-value svg {
          color: #6366f1;
        }

        /* Modal */
        .glass-overlay {
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(8px);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 24px;
        }

        .glass-modal {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
        }

        .modal-content {
          border-radius: 24px;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 32px;
          border-bottom: 1px solid rgba(148, 163, 184, 0.2);
        }

        .modal-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 800;
          color: #1e293b;
        }

        .modal-close {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: rgba(241, 245, 249, 0.8);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        .modal-body {
          padding: 32px;
          max-height: calc(90vh - 90px);
          overflow: auto;
          text-align: center;
        }

        .modal-body img {
          max-width: 100%;
          height: auto;
          border-radius: 16px;
          box-shadow: 0 12px 32px rgba(0,0,0,0.1);
        }

        /* Not Found */
        .not-found {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .not-found-content {
          text-align: center;
          padding: 64px 48px;
        }

        .not-found-icon {
          font-size: 80px;
          color: #cbd5e1;
          margin-bottom: 24px;
        }

        .not-found-content h2 {
          margin: 0 0 16px;
          font-size: 28px;
          font-weight: 800;
          color: #1e293b;
        }

        .not-found-content p {
          margin: 0 0 32px;
          color: #64748b;
          font-size: 16px;
          font-weight: 500;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 14px 32px;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
          transition: all 0.2s ease;
        }

        .back-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px rgba(99, 102, 241, 0.4);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .student-details {
            padding: 16px;
          }
          
          .hero-content {
            flex-direction: column;
            text-align: center;
            padding: 16px 0;
          }

          .student-meta {
            justify-content: center;
          }

          .contact-chips {
            justify-content: center;
          }

          .details-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .analysis-header {
            flex-direction: column;
            text-align: center;
          }

          .send-report-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default StudentDetails;
